/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InboxIcon from '@mui/icons-material/Inbox';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDDatePicker from 'components/MDDatePicker';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { useListUrlState } from '../../hooks/useListUrlState';
import BillQuickActionsMenu from './BillQuickActionsMenu';
import {
  DROPDOWN_SX,
  TEAL,
  TEAL_LIGHT,
  formatDisplayDate,
} from '../opdAppointments/constants';

const toISO = (d) => d.toISOString().split('T')[0];
// A billing list defaults to "today only" being wrong for finance users —
// they need to see the last month of activity, not just today's bills.
const defaultToDate = toISO(new Date());
const defaultFromDate = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toISO(d); })();

const HEADER_CELL_SX = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.92)',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  py: 1.3,
  whiteSpace: 'nowrap',
  borderBottom: 'none',
};

// Rounded, white-background inputs — matches the Accounts/Journal screens'
// filter bars, layered on top of the app-wide DROPDOWN_SX (which only sets
// height/font, not shape) rather than overriding it.
const FILTER_SELECT_SX = {
  ...DROPDOWN_SX,
  '& .MuiOutlinedInput-root': { ...DROPDOWN_SX['& .MuiOutlinedInput-root'], borderRadius: 3, background: '#fff' },
};
const FILTER_DATE_SX = { width: 150 };

const STATUS_CHIP_COLOR = {
  PENDING: 'warning',
  PAID: 'success',
  PARTIALLY_REFUNDED: 'info',
  REFUNDED: 'default',
  CANCELLED: 'error',
};

function zebraRowSx(index) {
  return {
    background: index % 2 === 0 ? '#fff' : '#fafcff',
    '&:hover': { background: TEAL_LIGHT },
    cursor: 'pointer',
  };
}

function patientName(bill) {
  const c = bill.contactInfo;
  if (!c) return `Patient #${bill.irm_contacts_id}`;
  return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' ') || `Patient #${bill.irm_contacts_id}`;
}

function doctorName(bill) {
  return bill.doctorInfo?.name || `Doctor #${bill.irm_users_id}`;
}

export default function OpdBilling() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [fromDate, setFromDate] = useState(urlParams?.get('from_date') || defaultFromDate);
  const [toDate, setToDate] = useState(urlParams?.get('to_date') || defaultToDate);
  const [doctorId, setDoctorId] = useState(urlParams?.get('doctor_id') || '');
  const [status, setStatus] = useState(urlParams?.get('status') || '');
  const [doctors, setDoctors] = useState([]);
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(urlParams ? Math.max(parseInt(urlParams.get('page'), 10) - 1, 0) || 0 : 0);
  const [limit, setLimit] = useState((urlParams && parseInt(urlParams.get('limit'), 10)) || 20);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.contacts
      .getDoctorsList()
      .then(resp => setDoctors(resp?.data || []))
      .catch(() => setDoctors([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    writeParams({
      from_date: fromDate,
      to_date: toDate,
      doctor_id: doctorId,
      status,
      page: page + 1,
      limit,
    });
    api.opdBilling
      .listBills({
        start_date: fromDate,
        end_date: toDate,
        doctor_id: doctorId || undefined,
        status: status || undefined,
        limit,
        offset: page * limit,
      })
      .then(resp => {
        setBills(resp?.data || []);
        setTotal(resp?.total || 0);
      })
      .catch(() => {
        setBills([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate, doctorId, status, page, limit, writeParams]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFromDateChange = (val) => { setFromDate(val || ''); setPage(0); };
  const handleToDateChange = (val) => { setToDate(val || ''); setPage(0); };
  const handleClearFilters = () => {
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    setDoctorId('');
    setStatus('');
    setPage(0);
  };
  const filtersActive = fromDate !== defaultFromDate || toDate !== defaultToDate || doctorId !== '' || status !== '';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <MDBox mb={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${TEAL} 0%, #005f70 100%)`,
                boxShadow: `0 4px 14px ${TEAL}44`,
              }}
            >
              <ReceiptLongIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <MDTypography variant="h4" fontWeight="bold" color="text">
                OPD Billing
              </MDTypography>
              <MDTypography variant="body2" color="text" opacity={0.7}>
                Registration &amp; consultation fee bills
              </MDTypography>
            </Box>
          </Box>
          {hasPermission('v1-opd-fee-config-manage') && (
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/opd/billing/fee-config')}
            >
              Fee Config
            </MDButton>
          )}
        </MDBox>

        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>
          <Box sx={{
            px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
            borderBottom: '1px solid #f0f4f8', background: 'linear-gradient(180deg, #fafcff 0%, #f5f9fb 100%)',
          }}>
            <Box display="flex" alignItems="center" gap={0.75} sx={{ mr: 0.5 }}>
              <FilterListIcon sx={{ fontSize: 19, color: TEAL }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#607d8b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Filters
              </Typography>
            </Box>
            <MDDatePicker label="From" value={fromDate} onChange={handleFromDateChange} maxDate={toDate || undefined} sx={FILTER_DATE_SX} />
            <MDDatePicker label="To" value={toDate} onChange={handleToDateChange} minDate={fromDate || undefined} sx={FILTER_DATE_SX} />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="opd-billing-doctor-label">Doctor</InputLabel>
              <Select
                labelId="opd-billing-doctor-label"
                label="Doctor"
                value={doctorId}
                onChange={e => { setDoctorId(e.target.value); setPage(0); }}
                sx={FILTER_SELECT_SX}
              >
                <MenuItem value="">All Doctors</MenuItem>
                {doctors.map(d => (
                  <MenuItem key={d.user_id} value={d.user_id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="opd-billing-status-label">Status</InputLabel>
              <Select
                labelId="opd-billing-status-label"
                label="Status"
                value={status}
                onChange={e => { setStatus(e.target.value); setPage(0); }}
                sx={FILTER_SELECT_SX}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="PARTIALLY_REFUNDED">Partially Refunded</MenuItem>
                <MenuItem value="REFUNDED">Refunded</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <Box display="flex" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
              {filtersActive && (
                <Chip
                  label="Clear Filters"
                  size="small"
                  clickable
                  onClick={handleClearFilters}
                  sx={{ fontSize: '0.68rem', fontWeight: 700, height: 30, px: 0.5, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px', '&:hover': { background: '#ffe4e4' } }}
                />
              )}
              <MDButton variant="gradient" color="logo" size="small" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: '20px' }}>
                Refresh
              </MDButton>
            </Box>
          </Box>

          <MDBox p={3}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: TEAL }} />
              </Box>
            ) : bills.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <InboxIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No OPD bills found</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ borderRadius: 3, border: '1px solid #eef2f5', overflow: 'hidden' }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, #005f70 100%)` }}>
                          <TableCell sx={HEADER_CELL_SX}>Bill No</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Patient</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Doctor</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Date</TableCell>
                          <TableCell align="right" sx={HEADER_CELL_SX}>Total</TableCell>
                          <TableCell align="right" sx={HEADER_CELL_SX}>Paid</TableCell>
                          <TableCell align="right" sx={HEADER_CELL_SX}>Balance</TableCell>
                          <TableCell sx={HEADER_CELL_SX}>Status</TableCell>
                          <TableCell sx={HEADER_CELL_SX} align="center">Actions</TableCell>
                        </TableRow>
                        {bills.map((bill, i) => (
                          <TableRow key={bill.id} sx={zebraRowSx(i)} onClick={() => navigate(`/opd/billing/${bill.id}`)}>
                            <TableCell sx={{ py: 1.3, fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>
                              {bill.bill_no || `#${bill.id}`}
                            </TableCell>
                            <TableCell sx={{ py: 1.3, fontSize: '0.82rem', fontWeight: 600, color: '#2d3748' }}>{patientName(bill)}</TableCell>
                            <TableCell sx={{ py: 1.3, fontSize: '0.78rem', color: '#546e7a' }}>{doctorName(bill)}</TableCell>
                            <TableCell sx={{ py: 1.3, fontSize: '0.75rem', color: '#78909c', whiteSpace: 'nowrap' }}>{formatDisplayDate(bill.bill_date)}</TableCell>
                            <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace' }}>₹{Number(bill.total_amount).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace', color: '#2e7d32' }}>₹{Number(bill.paid_amount).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ py: 1.3, fontSize: '0.8rem', fontFamily: 'monospace', color: Number(bill.balance_amount) > 0 ? '#c62828' : '#9aa5b1' }}>
                              ₹{Number(bill.balance_amount).toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 1.3 }}>
                              <Chip label={bill.status} size="small" color={STATUS_CHIP_COLOR[bill.status] || 'default'} sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20 }} />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.3 }} onClick={e => e.stopPropagation()}>
                              <BillQuickActionsMenu
                                bill={bill}
                                onSuccess={load}
                                onView={b => navigate(`/opd/billing/${b.id}`)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={limit}
                  onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
